import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { toast } from 'sonner';
import {
  Phone,
  WhatsappLogo,
  CaretDown,
  CaretUp,
  Trash,
  X,
  Lock,
  ClipboardText,
  MagnifyingGlass,
  CalendarBlank,
} from '@phosphor-icons/react';
import formatChatTime from '../utils/formatChatTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin's "Calling Agent Reports" section (Point 49).
 * Lists every report a Calling Handler has sent — handler name, range
 * label, sent date, activity count. Expandable to a per-activity table
 * (client snapshot columns + note + kind + time). Admin can permanently
 * delete a sent report after confirming their own login password.
 */
const CallingAgentReportsSection = ({ adminId }) => {
  const [reports, setReports] = useState(() => readInstantCache('calling_agent_reports', 'all') || []);
  const [loading, setLoading] = useState(() => !readInstantCache('calling_agent_reports', 'all'));
  const [expanded, setExpanded] = useState(() => new Set());
  const [deleteTarget, setDeleteTarget] = useState(null); // report object
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  // Issue 4 — admin-side filter by handler name + date.
  const [qHandler, setQHandler] = useState('');
  const [qDate, setQDate] = useState('');

  const filteredReports = reports.filter((r) => {
    const matchHandler = !qHandler || (r.handler_username || '').toLowerCase().includes(qHandler.toLowerCase());
    const matchDate = !qDate || (r.sent_at || '').slice(0, 10) === qDate || (r.range_label || '').includes(qDate);
    return matchHandler && matchDate;
  });

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/calling-agent-reports`);
      const _v = res.data?.reports || [];
      setReports(_v);
      writeInstantCache('calling_agent_reports', 'all', _v);
    } catch (e) {
      toast.error('Failed to load Calling Agent Reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submitDelete = async (e) => {
    e?.preventDefault?.();
    if (!deleteTarget) return;
    if (!password.trim()) { toast.error('Enter your admin password'); return; }
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/calling-agent-reports/${deleteTarget.id}`, {
        params: { admin_id: adminId, password },
      });
      toast.success('Report deleted');
      setDeleteTarget(null);
      setPassword('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-gray-500 text-sm">Loading reports…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="calling-agent-reports-section">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <ClipboardText size={22} weight="bold" className="text-teal-600" />
          Calling Agent Reports
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Every daily/weekly/custom activity report sent by a Calling Handler. Sent reports are
          permanent here unless deleted with your admin password.
        </p>
      </div>

      {reports.length === 0 ? (
        <div className="text-center py-10 text-gray-400 bg-white rounded-lg border border-gray-200">
          <ClipboardText size={40} weight="light" className="mx-auto mb-2" />
          <p>No reports sent yet</p>
        </div>
      ) : (
        <>
          {/* Issue 4 — filter bar */}
          <div className="bg-white rounded-lg border border-gray-200 p-3 flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-1.5">
              <MagnifyingGlass size={16} className="text-gray-400" />
              <input
                type="text"
                value={qHandler}
                onChange={(e) => setQHandler(e.target.value)}
                placeholder="Search by handler name…"
                className="flex-1 text-sm outline-none"
                data-testid="calling-report-filter-handler"
              />
            </div>
            <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-1.5">
              <CalendarBlank size={16} className="text-gray-400" />
              <input
                type="date"
                value={qDate}
                onChange={(e) => setQDate(e.target.value)}
                className="text-sm outline-none"
                data-testid="calling-report-filter-date"
              />
              {(qHandler || qDate) && (
                <button onClick={() => { setQHandler(''); setQDate(''); }} className="text-xs text-red-500 font-semibold" data-testid="calling-report-filter-clear">Clear</button>
              )}
            </div>
          </div>

          {filteredReports.length === 0 ? (
            <div className="text-center py-8 text-gray-400 bg-white rounded-lg border border-gray-200">
              <p>No reports match your filter</p>
            </div>
          ) : (
          <div className="space-y-3">
          {filteredReports.map((r) => {
            const isOpen = expanded.has(r.id);
            return (
              <div key={r.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`calling-report-row-${r.id}`}>
                <button
                  onClick={() => toggleExpanded(r.id)}
                  className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-gray-50"
                  data-testid={`calling-report-toggle-${r.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800 truncate">
                      {r.handler_username || 'Calling Handler'}
                      <span className="ml-2 text-xs font-normal text-gray-500">{r.range_label}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Sent {formatChatTime(r.sent_at)} · {r.activity_count} activit{r.activity_count === 1 ? 'y' : 'ies'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); setPassword(''); }}
                      className="p-2 rounded-full text-red-600 hover:bg-red-50"
                      title="Delete report (admin password required)"
                      data-testid={`calling-report-delete-btn-${r.id}`}
                    >
                      <Trash size={18} />
                    </button>
                    {isOpen ? <CaretUp size={18} className="text-gray-500" /> : <CaretDown size={18} className="text-gray-500" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 overflow-x-auto">
                    <table className="w-full text-xs min-w-[640px]">
                      <thead>
                        <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                          <th className="px-3 py-2 text-left">Kind</th>
                          <th className="px-3 py-2 text-left">Client</th>
                          <th className="px-3 py-2 text-left">Phone</th>
                          <th className="px-3 py-2 text-left">MB</th>
                          <th className="px-3 py-2 text-left">Serial / Client ID</th>
                          <th className="px-3 py-2 text-left">Time</th>
                          <th className="px-3 py-2 text-left">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(r.activities || []).map((a) => {
                          const snap = a.client_snapshot || {};
                          return (
                            <tr key={a.id} className="border-t border-gray-100" data-testid={`calling-report-activity-${a.id}`}>
                              <td className="px-3 py-2">
                                {a.kind === 'whatsapp'
                                  ? <span className="inline-flex items-center gap-1 text-green-600"><WhatsappLogo size={14} weight="fill" /> WhatsApp</span>
                                  : <span className="inline-flex items-center gap-1 text-blue-600"><Phone size={14} weight="fill" /> Call</span>}
                              </td>
                              <td className="px-3 py-2 text-gray-800">{snap.name || '—'}</td>
                              <td className="px-3 py-2 text-gray-600">{snap.phone || '—'}</td>
                              <td className="px-3 py-2 text-gray-600">{snap.mb_setup || '—'}</td>
                              <td className="px-3 py-2 text-gray-600">{snap.serial_number || snap.client_id || '—'}</td>
                              <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{formatChatTime(a.created_at)}</td>
                              <td className="px-3 py-2 text-gray-600 max-w-[220px] truncate" title={a.note || ''}>{a.note || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
          )}
        </>
      )}

      {/* Delete confirmation — admin's own password required */}
      {deleteTarget && (
        <div
          className="fixed inset-0 bg-black/70 z-[90] flex items-center justify-center p-3"
          onClick={(e) => { if (e.target === e.currentTarget) { setDeleteTarget(null); setPassword(''); } }}
        >
          <form onSubmit={submitDelete} className="bg-white rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 bg-red-100 text-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash size={18} weight="fill" />
              </div>
              <div className="flex-1">
                <div className="font-bold text-red-800 text-sm">Delete this report?</div>
                <div className="text-[11px] text-gray-600">
                  {deleteTarget.handler_username} · {deleteTarget.range_label} · permanent
                </div>
              </div>
              <button type="button" onClick={() => { setDeleteTarget(null); setPassword(''); }} className="p-1 hover:bg-gray-100 rounded">
                <X size={16} />
              </button>
            </div>

            <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
              <Lock size={10} /> Confirm with your admin password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              disabled={busy}
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
              placeholder="Admin login password"
              data-testid="calling-report-delete-password"
            />

            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => { setDeleteTarget(null); setPassword(''); }}
                disabled={busy}
                className="flex-1 py-2 rounded border border-gray-300 text-sm font-semibold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || !password.trim()}
                className="flex-1 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50"
                data-testid="calling-report-delete-confirm"
              >
                {busy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CallingAgentReportsSection;
